import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { updatePosition } from '@/modules/Employee/services'
import { Position } from '@/modules/Employee/types/response'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import KeycloakGroupSelect from '@/components/Form/Shared/KeycloakGroupSelect'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { updatePositionDefaultGroups } from '@/modules/Authorization/services'

export interface PositionEditModalProps extends ModalProps {
    position: Position
    refetch: () => void
}

interface FormValues {
    position_name: string
    position_is_executive: boolean
    keycloak_default_group_paths: string[]
}

const PositionEditModal: React.FC<PositionEditModalProps> = ({
    open,
    onClose,
    position,
    refetch,
}) => {
    const { t } = useTranslation('employee')
    const { isAdmin } = usePermissionsStore()

    const { control, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            position_name: position.position_name || '',
            position_is_executive: position.position_is_executive || false,
            keycloak_default_group_paths:
                position.keycloak_default_group_paths ??
                (position.keycloak_default_group_path
                    ? [position.keycloak_default_group_path]
                    : []),
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: updatePosition },
        {
            reactQueryKeys: QUERY_KEYS.POSITIONS,
            successMessage: 'position_updated_success',
            errorMessage: 'error_occurred',
        }
    )
    const {
        mutateAsync: updateDefaultGroup,
        isPending: isUpdatingDefaultGroup,
    } = useMutationWithHandler(
        {
            mutationFn: ({
                positionId,
                groupPaths,
            }: {
                positionId: number
                groupPaths: string[]
            }) => updatePositionDefaultGroups(positionId, groupPaths),
        },
        {
            reactQueryKeys: QUERY_KEYS.POSITIONS,
            successMessage: 'Đã cập nhật group mặc định',
            errorMessage: 'Không thể cập nhật group mặc định',
        }
    )

    const onSubmit = async (data: FormValues) => {
        const updatedPosition = await mutateAsync({
            position_id: position.position_id,
            position_name: data.position_name,
            department_id: position.department_id,
            position_is_executive: data.position_is_executive,
        })
        if (!updatedPosition) return

        if (isAdmin) {
            const updatedDefaultGroups = await updateDefaultGroup({
                positionId: position.position_id,
                groupPaths: data.keycloak_default_group_paths,
            })
            if (!updatedDefaultGroups) {
                refetch()
                return
            }
        }
        refetch()
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit_position')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-4 space-y-4">
                    <FormItem
                        control={control}
                        name="position_name"
                        type="textfield"
                        label={t('position_name')}
                        placeholder={t('enter_position_name')}
                    />

                    {isAdmin && (
                        <KeycloakGroupSelect
                            control={control}
                            name="keycloak_default_group_paths"
                            label="Group bổ sung theo chức vụ"
                        />
                    )}

                    <FormItem
                        control={control}
                        name="position_is_executive"
                        type="checkbox"
                        label={t('position_is_executive')}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            type="button"
                        >
                            {t('cancel')}
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            loading={isPending || isUpdatingDefaultGroup}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default PositionEditModal
